import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { ArrowLeft, FileText, Plus, Search, X } from "lucide-react";
import "../../CSS/Global.css";
import "../Equipments/ProductFormPage.css";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";
import Loader from "../Loader";
import "./InvoiceStyle.css";

function isQuoteRow(inv) {
  const t = String(inv?.document_type || "").toLowerCase();
  return t === "quote" || t === "proforma";
}

const formatDate = (d) => {
  if (!d || d === "0000-00-00") return "—";
  try {
    return new Date(`${d}T12:00:00`).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

export default function Quotes() {
  const navigate = useNavigate();
  const url = API_BASE_URL;
  const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
  const token = decryptToken("accessToken") || "";
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [convertingId, setConvertingId] = useState(null);

  const fetchQuotes = async () => {
    if (!isLoggedIn?.main_id) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${url}/newinvoice/all/${isLoggedIn.main_id}?document_type=quote`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setQuotes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error("Could not load quotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  const handleConvert = async (quote) => {
    const result = await Swal.fire({
      title: "Convert to invoice?",
      html: `
        <p style="margin-bottom:12px">Quote <strong>${quote.invoice_number}</strong> will become a published invoice with a new invoice number.</p>
        <label style="display:block;margin:8px 0;text-align:left">
          <input id="swal-send-mail" type="checkbox" /> Email invoice to customer
        </label>
        <label style="display:block;text-align:left">
          <input id="swal-send-whatsapp" type="checkbox" /> Send WhatsApp to customer
        </label>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Convert",
      preConfirm: () => ({
        send_mail: document.getElementById("swal-send-mail")?.checked || false,
        send_whatsapp: document.getElementById("swal-send-whatsapp")?.checked || false,
      }),
    });
    if (!result.isConfirmed) return;

    setConvertingId(quote.id);
    try {
      const res = await fetch(`${url}/newinvoice/convert-to-invoice/${quote.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          publish: true,
          send_mail: result.value?.send_mail === true,
          send_whatsapp: result.value?.send_whatsapp === true,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Conversion failed");
      toast.success(`Created invoice ${data.invoice?.invoice_number}`);
      await fetchQuotes();
      if (data.invoice?.id) {
        navigate(`/invoice-detail/${data.invoice.id}`);
      }
    } catch (err) {
      toast.error(err.message || "Could not convert quote");
    } finally {
      setConvertingId(null);
    }
  };

  const filtered = quotes.filter((q) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      String(q.invoice_number || "").toLowerCase().includes(s) ||
      String(q.customer_name || "").toLowerCase().includes(s)
    );
  });

  if (loading) return <Loader />;

  return (
    <div className="product-form-page product-form-page--quotes">
      <div className="oms-header product-form-page__header">
        <div className="product-form-page__title-wrap">
          <h1 className="dashboard-title">Quotes & proforma</h1>
          <p className="product-form-page__subtitle">
            Send estimates to customers before converting them to invoices.
          </p>
        </div>
        <div className="product-form-page__header-actions">
          <button
            type="button"
            className="product-form-page__back"
            onClick={() => navigate("/invoice")}
          >
            <ArrowLeft size={16} style={{ marginRight: 6 }} />
            Invoices
          </button>
          <button
            type="button"
            className="quotes-page__icon-btn"
            aria-label="Search quotes"
            onClick={() => setShowSearch((v) => !v)}
          >
            <Search size={18} />
          </button>
          <button type="button" className="oms-add-btn" onClick={() => navigate("/new-quote")}>
            <Plus size={16} style={{ marginRight: 4 }} />
            New quote
          </button>
        </div>
      </div>

      {showSearch && (
        <div className="quotes-page__search">
          <input
            type="search"
            className="quotes-page__search-input"
            placeholder="Search quote or customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <button
            type="button"
            className="quotes-page__search-clear"
            aria-label="Clear search"
            onClick={() => {
              setSearch("");
              setShowSearch(false);
            }}
          >
            <X size={18} />
          </button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="quotes-page__empty" role="status">
          <FileText size={40} strokeWidth={1.25} />
          <p>No quotes yet. Create one to send estimates before invoicing.</p>
          <button type="button" className="oms-add-btn" onClick={() => navigate("/new-quote")}>
            <Plus size={16} style={{ marginRight: 4 }} />
            Create your first quote
          </button>
        </div>
      ) : (
        <div className="quotes-page__table-wrap">
          <table className="quotes-page__table">
            <thead>
              <tr>
                <th>Quote #</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Valid until</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((q) => {
                const converted = Boolean(q.converted_to_id);
                const status = converted ? "Converted" : q.dpstatus || "Draft";
                return (
                  <tr key={q.id}>
                    <td>{q.invoice_number}</td>
                    <td>{q.customer_name}</td>
                    <td>{formatDate(q.invoice_date)}</td>
                    <td>{formatDate(q.due_date)}</td>
                    <td>₹{parseFloat(q.grand_total || 0).toFixed(2)}</td>
                    <td>
                      <span
                        className={`quotes-page__status quotes-page__status--${String(status).toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {status}
                      </span>
                    </td>
                    <td>
                      <div className="quotes-page__actions">
                        <button
                          type="button"
                          className="quotes-page__action-btn"
                          onClick={() => navigate(`/invoice-detail/${q.id}`)}
                        >
                          View
                        </button>
                        {!converted && isQuoteRow(q) ? (
                          <button
                            type="button"
                            className="quotes-page__action-btn quotes-page__action-btn--primary"
                            disabled={convertingId === q.id}
                            onClick={() => handleConvert(q)}
                          >
                            {convertingId === q.id ? "Converting…" : "Convert to invoice"}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
